import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, featuredArticles, hotArticles, faqs, stats } from '@/mocks/data';

function ArticleCard({ article }: { article: typeof featuredArticles[0] }) {
  const cat = categories.find((c) => c.id === article.categoryId);
  return (
    <article className="group bg-background-100/70 rounded-lg overflow-hidden hover:bg-background-100 transition-colors">
      <Link to={`/article/${article.id}`} className="block">
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {article.isFeatured && (
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
              精选
            </span>
          )}
        </div>
      </Link>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2.5">
          {cat && (
            <Link
              to={`/category/${cat.slug}`}
              className="px-2 py-0.5 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap"
            >
              {cat.name}
            </Link>
          )}
          <span className="text-xs text-foreground-400">{article.publishedAt}</span>
        </div>
        <Link to={`/article/${article.id}`} className="block mb-2">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount > 10000
                ? `${(article.readCount / 10000).toFixed(1)}万`
                : article.readCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-line"></i>
              {article.likeCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.commentCount}
            </span>
          </div>
          <Link
            to={`/article/${article.id}`}
            className="text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1"
          >
            阅读全文
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '爱情岛论坛独家提供·专属内容平台 - 最新最全优质内容资源';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        '爱情岛论坛独家提供最新、最全的优质内容资源，汇聚海量精选信息，为广大用户带来独一无二的专属体验。立即加入爱情岛论坛，畅享独家提供的丰富内容与社区互动服务。'
      );
    }
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) {
      metaKw.setAttribute(
        'content',
        '爱情岛论坛独家提供,爱情岛论坛最新内容,爱情岛独家资源,爱情岛论坛会员,爱情岛论坛入口,爱情岛论坛社区,爱情岛独家福利'
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://www.aiqingdao.com/');
    }
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '爱情岛论坛',
    url: 'https://www.aiqingdao.com',
    description: '爱情岛论坛独家提供最新、最全的优质内容资源，汇聚海量精选信息，为广大用户带来独一无二的专属体验。',
    sameAs: ['https://weibo.com/aiqingdao', 'https://weixin.qq.com/aiqingdao'],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '爱情岛论坛',
    url: 'https://www.aiqingdao.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.aiqingdao.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[680px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20romantic%20abstract%20landscape%20with%20soft%20sunset%20gradient%2C%20floating%20hearts%20and%20flowers%2C%20dreamy%20atmosphere%2C%20minimal%20art%20style%2C%20coral%20and%20amber%20color%20palette%2C%20gentle%20ethereal%20vibe%2C%20clean%20composition&width=1920&height=1080&seq=hero-bg-01&orientation=landscape"
            alt="爱情岛论坛"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-500/20 backdrop-blur-sm">
              <i className="ri-heart-3-fill text-primary-300 text-3xl md:text-4xl"></i>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
              爱情岛论坛
              <span className="block text-xl md:text-2xl lg:text-3xl font-normal mt-2 text-background-50/90">
                独家提供·专属内容平台
              </span>
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              爱情岛论坛独家提供最新、最全的优质内容资源，汇聚海量精选信息，为广大用户带来独一无二的专属体验
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即加入爱情岛论坛
              </a>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full border border-background-50/30 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="relative -mt-10 z-20 px-4 md:px-6">
          <div className="max-w-5xl mx-auto bg-background-50 rounded-xl border border-background-200/70 py-6 md:py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {[
                { value: '1,280+', label: '优质文章' },
                { value: '58.6万+', label: '注册会员' },
                { value: '3.2万+', label: '日活用户' },
                { value: '1280万+', label: '累计阅读' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16 md:pt-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              探索精彩内容
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              爱情岛论坛涵盖八大内容板块，满足你对优质内容的全部想象
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group bg-background-100/70 rounded-lg p-5 md:p-6 hover:bg-background-200/70 transition-colors text-center"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors">
                  <i className={`${cat.icon} text-primary-500 text-xl md:text-2xl`}></i>
                </div>
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-1">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-400">{cat.articleCount} 篇文章</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Articles */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16 md:pt-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                精选推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                爱情岛论坛编辑团队精心挑选的优质内容
              </p>
            </div>
            <Link
              to="/category/qinggan"
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              查看更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredArticles.slice(0, 6).map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </section>

        {/* Hot Articles */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-12 md:pt-16">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-fire-fill text-accent-500"></i>
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  热门内容
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  社区热议，不容错过的精彩文章
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {hotArticles.slice(0, 4).map((article) => (
              <ArticleCard key={`hot-${article.id}`} article={article} />
            ))}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16 md:pt-20">
          <div className="relative rounded-xl overflow-hidden py-14 md:py-20 px-6 text-center">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20soft%20flowing%20shapes%2C%20coral%20and%20amber%20tones%2C%20dreamy%20atmosphere%2C%20minimal%20geometric%20style%2C%20gentle%20elegant%20composition&width=1600&height=900&seq=cta-bg-01&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-800/70 via-primary-700/60 to-accent-700/70"></div>
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-4">
                加入爱情岛论坛，畅享独家福利
              </h2>
              <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-lg mx-auto">
                成为爱情岛论坛会员，获取独家内容资源、参与社区互动、享受专属福利
              </p>
              <a
                href="#"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-primary-600 font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即注册
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500">
              关于爱情岛论坛的常见疑问解答
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-background-100/70 rounded-lg overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/70 transition-colors">
                  <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                    {faq.question}
                  </span>
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-foreground-400 group-open:rotate-45 transition-transform">
                    <i className="ri-add-line"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}